import {
  describeRpcError,
  isAuthorizationError,
  useMyMemberships,
  useOpenChurchApp,
  useTenant,
} from "@how/app-kit";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCalendarOff, IconCheck, IconExternalLink, IconPlus, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { semanticColor } from "@how/design-system";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
  useFocusWhenReady,
  useReturnFocus,
  DISPLAY_LOCALE,
} from "@how/ui";
import { env } from "../../lib/env";
import { useTenantRecord } from "../tenant/queries";
import { SharingCard } from "./BusySharing";
import {
  useDeleteBlockout,
  useMyScheduleEverywhere,
  useRespond,
  useSetBlockouts,
  type MyScheduleEverywhere,
  type ScheduleAssignment,
  type ScheduleBlockout,
  type ScheduleChurch,
} from "./my-schedule-queries";
import {
  findConflicts,
  formatWhenAt,
  itemPath,
  listChurchNames,
  openInLabel,
  splitForPage,
  targetItem,
  type LabelledBlockout,
} from "./schedule-model";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? semanticColor("warning") : semanticColor("danger"),
    title,
    message: described.message,
  });
}

function dateRange(b: ScheduleBlockout): string {
  const f = (d: string) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(DISPLAY_LOCALE, {
      day: "numeric",
      month: "short",
    });
  return b.startsOn === b.endsOn ? f(b.startsOn) : `${f(b.startsOn)} to ${f(b.endsOn)}`;
}

/**
 * My schedule (spec 2026-10-08 piece 3; ADR 0070): every date the person serves, at every church, each labelled with
 * its church. This church's dates are answered here; another church's open in that church's portal, at the date.
 * Blockouts can be set for one church or all of them (ADR 0066, kept by ruling S1). Everything is resolved from the
 * login on the server.
 */
export function MySchedulePage() {
  const schedule = useMyScheduleEverywhere();
  const tenant = useTenant();
  const record = useTenantRecord();
  const current = tenant.status === "resolved" ? tenant.tenant.tenantId : "";
  return (
    <>
      <PageHeader
        title="My schedule"
        description="Your serving dates at every church you serve at. Answer this church's dates here."
      />
      {schedule.isPending ? (
        <LoadingState rows={4} label="Loading your schedule" />
      ) : schedule.isError ? (
        isAuthorizationError(schedule.error) ||
        describeRpcError(schedule.error).kind === "denied" ? (
          <PermissionDenied />
        ) : (
          <ErrorState
            message="Could not load your schedule."
            onRetry={() => {
              void schedule.refetch();
            }}
          />
        )
      ) : schedule.data.churches.length === 0 && schedule.data.needsTwoStep.length === 0 ? (
        <EmptyState
          title="No record is linked to your login yet"
          description="Ask your church to link your login to your record; your serving dates appear here after that."
        />
      ) : (
        <ScheduleBody
          data={schedule.data}
          currentTenantId={current}
          churchName={record.data?.name ?? "this church"}
        />
      )}
    </>
  );
}

function ScheduleBody({
  data,
  currentTenantId,
  churchName,
}: {
  data: MyScheduleEverywhere;
  currentTenantId: string;
  churchName: string;
}) {
  const names = new Map(data.churches.map((c) => [c.tenantId, c.name]));
  const slugs = new Map(data.churches.map((c) => [c.tenantId, c.slug]));
  const conflicts = findConflicts(data.assignments, names);
  const page = splitForPage(data, currentTenantId);
  const respond = useRespond();
  // Another church's portal opens at the address that church prefers (R9, ADR 0067 A8).
  const openApp = useOpenChurchApp(env);
  const memberships = useMyMemberships();
  const [declining, setDeclining] = useState<ScheduleAssignment | null>(null);
  const [addingBlockout, setAddingBlockout] = useState(false);
  const [removing, setRemoving] = useState<LabelledBlockout | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const focusWhenReady = useFocusWhenReady();
  // The dates dialog is keyed to start empty each time, so it unmounts on close; focus goes back by hand (#285).
  const blockoutFocus = useReturnFocus();
  const { search, hash } = useLocation();
  const target = targetItem(search);
  // Derived during render (ruling S4): the address names a date that is not on the list any more.
  const [dismissedTarget, setDismissedTarget] = useState<string | null>(null);
  const shown = [...page.pending, ...page.accepted, ...page.declined];
  const pendingHere = page.pending.filter((a) => a.tenantId === currentTenantId);
  const pendingElsewhere = page.pending.filter((a) => a.tenantId !== currentTenantId);
  const targetGone =
    target !== null && target !== dismissedTarget && !shown.some((a) => a.id === target);

  const handoffFailed = openApp.error !== null;
  useEffect(() => {
    if (handoffFailed) {
      notifications.show({
        color: semanticColor("danger"),
        title: "Could not open that address",
        message: "That address could not be opened. Try again.",
      });
    }
  }, [handoffFailed]);

  // Arriving from another church's "Open in" (R5): show that date and put focus on it, once the list has drawn.
  useEffect(() => {
    if (target !== null) {
      const el = document.querySelector<HTMLElement>(`[data-assignment="${target}"]`);
      if (el === null) return;
      el.scrollIntoView({ block: "center" });
      el.focus();
      return;
    }
    if (hash === "#privacy") document.getElementById("privacy")?.scrollIntoView({ block: "start" });
  }, [target, hash]);

  const openElsewhere = (tenantId: string, path: string) => {
    const slug = slugs.get(tenantId);
    if (slug !== undefined) openApp.open({ tenantId, slug, app: "member_portal", path });
  };

  const answer = (a: ScheduleAssignment, accepted: boolean, note?: string) => {
    respond.mutate(
      { assignmentId: a.id, accepted, ...(note === undefined ? {} : { note }) },
      {
        onSuccess: () => {
          // The date moves to "You are on" or "You said no"; focus goes with it (issue #285).
          focusWhenReady(
            `[data-assignment="${a.id}"][data-response="${accepted ? "accepted" : "declined"}"] button`,
          );
          notifications.show({
            color: accepted ? semanticColor("success") : semanticColor("neutral"),
            title: accepted ? `You are on: ${a.position}` : "Noted, thank you",
            message: `${a.title} · ${formatWhenAt(a.startsAt, a.timeZone)}`,
          });
          setDeclining(null);
        },
        onError: (error) => {
          notifyError("Your answer was not saved", error);
        },
      },
    );
  };

  const cardFor = (a: ScheduleAssignment, actions: { yes?: boolean; no?: boolean }) => {
    const here = a.tenantId === currentTenantId;
    const other = conflicts.get(a.id);
    return (
      <AssignmentCard
        key={a.id}
        assignment={a}
        churchName={names.get(a.tenantId) ?? ""}
        churchSlug={slugs.get(a.tenantId) ?? ""}
        here={here}
        targeted={a.id === target}
        alsoServing={other === undefined ? null : listChurchNames(other)}
        busy={respond.isPending}
        {...(here && actions.yes === true
          ? {
              onYes: () => {
                answer(a, true);
              },
            }
          : {})}
        {...(here && actions.no === true
          ? {
              onNo: () => {
                setDeclining(a);
              },
            }
          : {})}
        {...(here
          ? {}
          : {
              onOpenElsewhere: () => {
                openElsewhere(a.tenantId, itemPath(a.id));
              },
            })}
      />
    );
  };

  return (
    <Stack gap="xl">
      {data.needsTwoStep.map((c) => (
        <Alert
          key={c.slug}
          color={semanticColor("info")}
          title={`${c.name} needs your two-step code`}
        >
          <Stack gap="xs" align="flex-start">
            <Text size="sm">Enter your two-step code at {c.name} to include it here.</Text>
            <Button
              size="compact-sm"
              variant="light"
              rightSection={<IconExternalLink size={14} />}
              onClick={() => {
                // The notice names the church by slug only; the person's own list gives its id. A church it cannot
                // name opens at HoW's address (A6).
                const tenantId = memberships.data?.find((m) => m.slug === c.slug)?.tenantId ?? null;
                openApp.open({
                  tenantId,
                  slug: c.slug,
                  app: "member_portal",
                  path: "/my-schedule",
                });
              }}
            >
              {openInLabel(c.name)}
            </Button>
          </Stack>
        </Alert>
      ))}
      {targetGone ? (
        <Alert
          color={semanticColor("info")}
          withCloseButton
          closeButtonLabel="Dismiss"
          onClose={() => {
            setDismissedTarget(target);
          }}
        >
          That date is no longer on your schedule.
        </Alert>
      ) : null}
      {notice !== null ? (
        <Alert
          color={semanticColor("warning")}
          withCloseButton
          closeButtonLabel="Dismiss"
          onClose={() => {
            setNotice(null);
          }}
        >
          {notice}
        </Alert>
      ) : null}
      <SharingCard data={data} />
      {page.linkedHere ? null : (
        <Alert color={semanticColor("info")}>
          {`Your login isn't linked to a record at ${churchName} yet. Ask the church office to link it; your dates there appear here after that.`}
        </Alert>
      )}
      <section aria-labelledby="my-schedule-pending">
        <Title order={2} size="h4" id="my-schedule-pending" mb="xs">
          {page.pendingHere > 0
            ? `Waiting for your answer (${String(page.pendingHere)})`
            : "Waiting for your answer"}
        </Title>
        {page.messageHere !== null ? (
          <Text size="sm" c="dimmed" mb="sm">
            From your scheduler: &ldquo;{page.messageHere}&rdquo;
          </Text>
        ) : null}
        {page.pending.length === 0 ? (
          <Text size="sm" c="dimmed">
            Nothing waiting. Thank you.
          </Text>
        ) : (
          <Stack gap="sm">
            {pendingHere.map((a) => cardFor(a, { yes: true, no: true }))}
            {pendingElsewhere.length === 0 ? null : (
              // Other churches' waiting dates are not counted above and are answered there (ruling S4; Minor 10).
              <Stack gap="sm" data-elsewhere mt={pendingHere.length === 0 ? 0 : "xs"}>
                <Text size="sm" fw={600} c="dimmed">
                  Answer in that church&apos;s portal
                </Text>
                {pendingElsewhere.map((a) => cardFor(a, {}))}
              </Stack>
            )}
          </Stack>
        )}
      </section>
      <section aria-labelledby="my-schedule-accepted">
        <Title order={2} size="h4" id="my-schedule-accepted" mb="xs">
          You are on
        </Title>
        {page.accepted.length === 0 ? (
          <Text size="sm" c="dimmed">
            No dates yet.
          </Text>
        ) : (
          <Stack gap="sm">{page.accepted.map((a) => cardFor(a, { no: true }))}</Stack>
        )}
      </section>
      {page.declined.length > 0 ? (
        <section aria-labelledby="my-schedule-declined">
          <Title order={2} size="h4" id="my-schedule-declined" mb="xs">
            You said no
          </Title>
          <Stack gap="sm">{page.declined.map((a) => cardFor(a, { yes: true }))}</Stack>
        </section>
      ) : null}
      <section aria-labelledby="my-schedule-blockouts">
        <Group justify="space-between" align="center" mb="xs">
          <Title order={2} size="h4" id="my-schedule-blockouts">
            Dates you cannot serve
          </Title>
          {data.churches.length > 0 ? (
            <Button
              variant="light"
              size="compact-sm"
              leftSection={<IconPlus size={14} />}
              data-add-dates
              onClick={() => {
                blockoutFocus.remember();
                setAddingBlockout(true);
              }}
            >
              Add dates
            </Button>
          ) : null}
        </Group>
        {page.blockouts.length === 0 ? (
          <Text size="sm" c="dimmed">
            None ahead.
          </Text>
        ) : (
          <Stack gap="xs">
            {page.blockouts.map((b) => (
              <Card key={b.id} padding="sm" data-blockout={b.id}>
                <Group justify="space-between" wrap="nowrap">
                  <div>
                    <Text size="xs" fw={600} c="dimmed">
                      {b.churchName}
                    </Text>
                    <Text size="sm" fw={500}>
                      {dateRange(b)}
                    </Text>
                    {b.reason === null ? null : (
                      <Text size="xs" c="dimmed">
                        {b.reason}
                      </Text>
                    )}
                  </div>
                  <Button
                    variant="subtle"
                    color="gray"
                    size="compact-sm"
                    aria-label={`Remove ${dateRange(b)} at ${b.churchName}`}
                    onClick={() => {
                      setRemoving(b);
                    }}
                  >
                    Remove
                  </Button>
                </Group>
              </Card>
            ))}
          </Stack>
        )}
      </section>

      <DeclineModal
        assignment={declining}
        busy={respond.isPending}
        onClose={() => {
          setDeclining(null);
        }}
        onConfirm={(note) => {
          if (declining !== null) answer(declining, false, note);
        }}
      />
      <BlockoutModal
        key={addingBlockout ? "open" : "closed"}
        opened={addingBlockout}
        churches={data.churches}
        currentTenantId={currentTenantId}
        onClose={() => {
          setAddingBlockout(false);
          blockoutFocus.restore();
        }}
        onPartial={(message) => {
          setAddingBlockout(false);
          setNotice(message);
          blockoutFocus.restore();
        }}
      />
      <RemoveBlockoutModal
        blockout={removing}
        onClose={() => {
          setRemoving(null);
        }}
      />
    </Stack>
  );
}

function AssignmentCard({
  assignment: a,
  churchName,
  churchSlug,
  here,
  targeted,
  alsoServing,
  busy,
  onYes,
  onNo,
  onOpenElsewhere,
}: {
  assignment: ScheduleAssignment;
  churchName: string;
  churchSlug: string;
  here: boolean;
  targeted: boolean;
  alsoServing: string | null;
  busy: boolean;
  onYes?: () => void;
  onNo?: () => void;
  onOpenElsewhere?: () => void;
}) {
  const when = formatWhenAt(a.startsAt, a.timeZone);
  return (
    <Card
      data-assignment={a.id}
      data-response={a.response}
      data-church={churchSlug}
      // Focusable only when the address named it (R5), so it can take focus on arrival.
      {...(targeted ? { tabIndex: -1 } : {})}
    >
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <div>
            <Text size="sm" fw={600} c="dimmed">
              {churchName}
            </Text>
            <Text fw={600}>{when}</Text>
            <Text size="sm">
              {a.position} · {a.team} team
            </Text>
            <Text size="sm" c="dimmed">
              {a.title}
              {a.location === null ? "" : ` · ${a.location}`}
              {a.season === null ? "" : ` · ${a.season}`}
            </Text>
          </div>
          <Group gap="xs" wrap="wrap">
            {a.conflict ? (
              <Badge
                variant="light"
                color={semanticColor("warning")}
                leftSection={<IconCalendarOff size={12} />}
              >
                You are blocked out
              </Badge>
            ) : null}
            {a.response === "accepted" ? (
              <Badge
                variant="light"
                color={semanticColor("success")}
                leftSection={<IconCheck size={12} />}
              >
                Yes
              </Badge>
            ) : a.response === "declined" ? (
              <Badge
                variant="light"
                color={semanticColor("neutral")}
                leftSection={<IconX size={12} />}
              >
                No
              </Badge>
            ) : here ? null : (
              <Badge variant="light" color={semanticColor("neutral")}>
                Waiting for you
              </Badge>
            )}
          </Group>
        </Group>
        {alsoServing !== null ? (
          <Text size="sm" c={semanticColor("warning")}>
            {`Also serving at ${alsoServing} then`}
          </Text>
        ) : null}
        {a.response === "declined" && a.note !== null ? (
          <Text size="xs" c="dimmed">
            You said: {a.note}
          </Text>
        ) : null}
        <Group gap="sm" justify="flex-end" wrap="wrap">
          {onNo !== undefined ? (
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              disabled={busy}
              aria-label={`Say no to ${a.position} on ${when}`}
              onClick={onNo}
            >
              {a.response === "accepted" ? "Change to no" : "No, I can't"}
            </Button>
          ) : null}
          {onYes !== undefined ? (
            <Button
              size="compact-sm"
              loading={busy}
              aria-label={`Say yes to ${a.position} on ${when}`}
              onClick={onYes}
            >
              {a.response === "declined" ? "Change to yes" : "Yes, I'll be there"}
            </Button>
          ) : null}
          {onOpenElsewhere !== undefined ? (
            <Button
              size="compact-sm"
              variant="light"
              rightSection={<IconExternalLink size={14} />}
              onClick={onOpenElsewhere}
            >
              {openInLabel(churchName)}
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Card>
  );
}

function DeclineModal({
  assignment,
  busy,
  onClose,
  onConfirm,
}: {
  assignment: ScheduleAssignment | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
}) {
  // The note belongs to one date (Minor 6): another date, or the same one after Cancel, starts empty. Kept in state
  // rather than keyed, so the dialog stays mounted and closes with its transition.
  const [draft, setDraft] = useState<{ readonly id: string | null; readonly note: string }>({
    id: null,
    note: "",
  });
  const note = assignment !== null && draft.id === assignment.id ? draft.note : "";
  const close = () => {
    setDraft({ id: null, note: "" });
    onClose();
  };
  return (
    <Modal
      opened={assignment !== null}
      onClose={close}
      title={
        assignment === null
          ? ""
          : `Say no to ${formatWhenAt(assignment.startsAt, assignment.timeZone)}`
      }
      radius="lg"
    >
      <Stack gap="sm">
        <Text size="sm">
          {assignment === null ? "" : `${assignment.position}, ${assignment.team} team. `}
          Your scheduler sees your answer straight away and can find someone else.
        </Text>
        <Textarea
          label="A word for your scheduler"
          placeholder="Away that weekend"
          autosize
          minRows={2}
          maxLength={500}
          value={note}
          onChange={(e) => {
            setDraft({ id: assignment?.id ?? null, note: e.currentTarget.value });
          }}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button
            loading={busy}
            onClick={() => {
              onConfirm(note);
            }}
          >
            Say no
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Dates you cannot serve, for one church or all of them (ADR 0066; kept by ruling S1). With two or more churches it
 * asks which, starting on the church being viewed; each church saves through its own set_blockout, and a church that
 * refuses is named rather than hiding the ones that saved.
 */
function BlockoutModal({
  opened,
  churches,
  currentTenantId,
  onClose,
  onPartial,
}: {
  opened: boolean;
  churches: readonly ScheduleChurch[];
  currentTenantId: string;
  onClose: () => void;
  onPartial: (message: string) => void;
}) {
  const set = useSetBlockouts();
  const several = churches.length > 1;
  const names = new Map(churches.map((c) => [c.tenantId, c.name]));
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState<"one" | "all">("one");
  const [church, setChurch] = useState<string | null>(
    churches.some((c) => c.tenantId === currentTenantId)
      ? currentTenantId
      : (churches[0]?.tenantId ?? null),
  );
  const ready = startsOn !== "" && (endsOn === "" || endsOn >= startsOn);
  // One church offered, and not the one being viewed (the login isn't linked here): name it, so nobody saves dates for
  // a church they did not mean.
  const onlyElsewhere =
    churches.length === 1 && churches[0]?.tenantId !== currentTenantId ? churches[0] : undefined;
  const save = async () => {
    const tenantIds =
      scope === "all" ? churches.map((c) => c.tenantId) : church === null ? [] : [church];
    let r: Awaited<ReturnType<typeof set.mutateAsync>>;
    try {
      r = await set.mutateAsync({
        tenantIds,
        startsOn,
        endsOn: endsOn === "" ? startsOn : endsOn,
        reason,
      });
    } catch (error) {
      notifyError("The dates were not saved", error);
      return;
    }
    if (r.failed.length === 0) {
      onClose();
      return;
    }
    if (r.saved.length === 0 && r.failed.length === 1) {
      // One church, refused: say why, and keep the dialog open to try again.
      // The database's code travels with it (Minor 8), so a refusal reads as a warning and a fault as an error.
      notifyError("The dates were not saved", r.failed[0]);
      return;
    }
    const saved = listChurchNames(r.saved.map((id) => names.get(id) ?? ""));
    const failed = listChurchNames(r.failed.map((f) => names.get(f.tenantId) ?? ""));
    // Close the dialog so a retry is a fresh single-church action and never re-saves a church that succeeded.
    onPartial(
      r.saved.length === 0
        ? `Not saved for ${failed}: try again from that church.`
        : `Saved for ${saved}. Not saved for ${failed}: try again from that church.`,
    );
  };
  return (
    <Modal opened={opened} onClose={onClose} title="Dates you cannot serve" radius="lg">
      <Stack gap="sm">
        <Group grow>
          <TextInput
            label="From"
            type="date"
            required
            value={startsOn}
            onChange={(e) => {
              setStartsOn(e.currentTarget.value);
            }}
          />
          <TextInput
            label="To"
            type="date"
            description="Leave empty for one day."
            value={endsOn}
            onChange={(e) => {
              setEndsOn(e.currentTarget.value);
            }}
          />
        </Group>
        <TextInput
          label="Why (optional)"
          placeholder="Holiday, work, family"
          maxLength={200}
          value={reason}
          onChange={(e) => {
            setReason(e.currentTarget.value);
          }}
        />
        {onlyElsewhere === undefined ? null : (
          <Text size="sm" fw={600}>
            For {onlyElsewhere.name}
          </Text>
        )}
        {several ? (
          <Radio.Group
            label="For"
            value={scope}
            onChange={(v) => {
              setScope(v === "all" ? "all" : "one");
            }}
          >
            <Stack gap="xs" mt="xs">
              <Radio value="one" label="One church" />
              <Radio value="all" label="All my churches" />
            </Stack>
          </Radio.Group>
        ) : null}
        {several && scope === "one" ? (
          <Select
            label="Church"
            data={churches.map((c) => ({ value: c.tenantId, label: c.name }))}
            value={church}
            onChange={setChurch}
            allowDeselect={false}
          />
        ) : null}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!ready || (scope === "one" && church === null)}
            loading={set.isPending}
            onClick={() => void save()}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RemoveBlockoutModal({
  blockout,
  onClose,
}: {
  blockout: LabelledBlockout | null;
  onClose: () => void;
}) {
  const remove = useDeleteBlockout();
  const focusWhenReady = useFocusWhenReady();
  return (
    <ConfirmActionModal
      opened={blockout !== null}
      onClose={onClose}
      title="Remove these dates"
      message={
        blockout === null
          ? ""
          : `Remove ${dateRange(blockout)} at ${blockout.churchName}? Your scheduler may ask you for those dates again.`
      }
      confirmLabel="Remove"
      loading={remove.isPending}
      onConfirm={() => {
        if (blockout === null) return;
        remove.mutate(blockout.id, {
          onSuccess: () => {
            // The dates leave the list; focus goes to Add dates once they have (issue #285).
            focusWhenReady("[data-add-dates]", { gone: `[data-blockout="${blockout.id}"]` });
            onClose();
          },
          onError: (error) => {
            notifyError("The dates were not removed", error);
          },
        });
      }}
    />
  );
}
